// FFL (Federal Firearms License) helpers — number parsing, formatting,
// expiration calculation, frequency analysis, and ATF eZ Check scraping.

import { format } from 'date-fns';
import { FFL } from '../models/general';
import { DateFormats, Questions } from '../models/constants';
import { AtfFfl } from '../models/atf-ffl';

export function findFfl(sharedService: any, searchForFfl: string) {
  let fflFound: FFL;
  for (const ffl of sharedService.ffl_list) {
    if (ffl.ffl === searchForFfl) {
      fflFound = ffl;
      sharedService.openYesNoModal(Questions.correct_ffl, fflFound, true);
    }
  }
}

export function calculateFFLExpirationDate(ffl: string) {
  if (!ffl) return null;
  ffl = ffl.toUpperCase();
  let expiration_year: string;
  let expiration_month: string;
  const current_year = new Date().getFullYear();
  const parsed_year = ffl.substring(12, 13);
  const parsed_month = ffl.substring(13, 14);
  if (parsed_month === 'A') {
    expiration_month = 'January';
  } else if (parsed_month === 'B') {
    expiration_month = 'February';
  } else if (parsed_month === 'C') {
    expiration_month = 'March';
  } else if (parsed_month === 'D') {
    expiration_month = 'April';
  } else if (parsed_month === 'E') {
    expiration_month = 'May';
  } else if (parsed_month === 'F') {
    expiration_month = 'June';
  } else if (parsed_month === 'G') {
    expiration_month = 'July';
  } else if (parsed_month === 'H') {
    expiration_month = 'August';
  } else if (parsed_month === 'J') {
    expiration_month = 'September';
  } else if (parsed_month === 'K') {
    expiration_month = 'October';
  } else if (parsed_month === 'L') {
    expiration_month = 'November';
  } else if (parsed_month === 'M') {
    expiration_month = 'December';
  }
  const last_digit = current_year.toString().substring(3, 4);
  if (parsed_year >= last_digit) {
    expiration_year = current_year.toString().substring(0, 3) + parsed_year;
  } else {
    let decade = current_year.toString().substring(2, 3);
    expiration_year =
      current_year.toString().substring(0, 2) +
      (+decade + 1).toString() +
      parsed_year.toString();
  }
  return format(
    new Date(`${expiration_month} 1, ${expiration_year}`),
    DateFormats.M_D_Y,
  );
}

export function validateFflRegex(ffl: string) {
  if (ffl.length === 20) {
    const regex =
      /[0-9][0-9][0-9][0-9][0-9][0-9][0-9][0-9][0-9][A-Z][0-9][0-9][0-9][0-9][0-9]/;
    return regex.test(ffl.toUpperCase());
  } else return null;
}

export function formatFfl(ffl: string): string {
  let formattedFfl = ffl;
  formattedFfl =
    ffl.substring(0, 1) +
    '-' +
    ffl.substring(1, 3) +
    '-' +
    ffl.substring(3, 6) +
    '-' +
    ffl.substring(6, 8) +
    '-' +
    ffl.substring(8, 10) +
    '-' +
    ffl.substring(10, 15);
  return formattedFfl;
}

export function createCompanyNumber(sharedService: any): string {
  if (sharedService.company_info && sharedService.company_info.ffl) {
    const ffl = sharedService.company_info.ffl.replace(/-/g, '');
    return ffl.substring(0, 3) + ffl.substring(10, 15);
  } else {
    return '';
  }
}

export function longToShortFFL(input: string): string {
  if (!input) return '';
  // Remove all hyphens
  const cleanedString = input.replace(/-/g, '');

  // Extract the first 3 characters
  const firstPart = cleanedString.substring(0, 3);

  // Extract the last 5 characters
  const lastPart = cleanedString.substring(cleanedString.length - 5);

  // Combine the extracted parts
  return firstPart + lastPart;
}

export function shortToLongFFL(sharedService: any, short_ffl: string): FFL {
  for (const entry of sharedService.ffl_list) {
    if (short_ffl === longToShortFFL(entry.ffl)) {
      console.log(entry);
      return entry;
    }
  }
  return null;
}

export function getTopFiveFFLs(sharedService: any) {
  sharedService.ffls_frequently_used = [];
  const most_used_ffls: string[] = getTop5MostUsedFFL(sharedService);

  for (const ffl of sharedService.ffl_list) {
    const shortFfl = longToShortFFL(ffl.ffl);
    if (most_used_ffls.includes(shortFfl)) {
      sharedService.ffls_frequently_used.push(ffl);
    }
  }

  // Sort frequently used by the order of their frequency
  sharedService.ffls_frequently_used.sort((a: any, b: any) => {
    return (
      most_used_ffls.indexOf(longToShortFFL(a.ffl)) -
      most_used_ffls.indexOf(longToShortFFL(b.ffl))
    );
  });

  // Limit back down to 5 (in case the filtering caused us to look deeper or if we just want max 5)
  sharedService.ffls_frequently_used = sharedService.ffls_frequently_used.slice(
    0,
    5,
  );
}

function getTop5MostUsedFFL(sharedService: any): string[] {
  const boundBook = sharedService.bound_book;

  // Count the occurrences of each a_ffl
  const aFflCount: { [key: string]: number } = boundBook.reduce(
    (acc: { [key: string]: number }, entry) => {
      if (!entry.a_ffl) return acc;
      const shorthand_ffl = longToShortFFL(entry.a_ffl);
      acc[shorthand_ffl] = (acc[shorthand_ffl] || 0) + 1;
      return acc;
    },
    {},
  );

  // Convert the count object to an array of [a_ffl, count] pairs
  const aFflCountArray = Object.entries(aFflCount);

  // Sort the array by count in descending order
  aFflCountArray.sort((a, b) => b[1] - a[1]);

  // Extract the top 10 a_ffl values so that `getTopFiveFFLs` can pull up to 5 unique ones even if some are already in 'last used'
  const topFFL = aFflCountArray.slice(0, 10).map((entry) => entry[0]);
  return topFFL;
}

export function getLastFiveFFLs(sharedService: any) {
  const last_ffls_used: string[] = getTop5FflIds(sharedService);
  sharedService.last_ffls_used = sharedService.ffl_list.filter((ffl) =>
    last_ffls_used.includes(longToShortFFL(ffl.ffl)),
  );
  // Maintain chronological sort order from getTop5FflIds
  sharedService.last_ffls_used.sort((a: any, b: any) => {
    return (
      last_ffls_used.indexOf(longToShortFFL(a.ffl)) -
      last_ffls_used.indexOf(longToShortFFL(b.ffl))
    );
  });
  sharedService.last_ffls_used = [...sharedService.last_ffls_used];
}

function getTop5FflIds(sharedService: any): string[] {
  // Sort a shallow copy so we don't mutate the original array
  const sortedBoundBook = [...sharedService.bound_book].sort(
    (a, b) => b.firearm_id - a.firearm_id,
  );

  const topFfls: string[] = [];
  for (const entry of sortedBoundBook) {
    if (entry.a_ffl) {
      const shortFfl = longToShortFFL(entry.a_ffl);
      if (!topFfls.includes(shortFfl)) {
        topFfls.push(shortFfl);
      }
      if (topFfls.length === 5) {
        break;
      }
    }
  }
  return topFfls;
}

export function parseFflEzResult(html: string): AtfFfl {
  const doc = new DOMParser().parseFromString(html, 'text/html');

  // helper: convert innerHTML -> text while preserving <br/> as newlines
  const htmlToTextPreserveBreaks = (htmlFragment: string): string => {
    const replaced = htmlFragment.replace(/<br\s*\/?>/gi, '\n');
    const tmp = document.createElement('div');
    tmp.innerHTML = replaced;
    // collapse multiple blank lines/spaces, trim edges
    return (
      tmp.textContent
        ?.replace(/\r/g, '')
        .replace(/\n{2,}/g, '\n')
        .trim() ?? ''
    );
  };

  const mapping: Record<string, string> = {};

  // iterate table rows looking for label/value pairs (label often in first td, value in second)
  const rows = doc.querySelectorAll('table tr');
  rows.forEach((tr) => {
    const tds = tr.querySelectorAll('td');
    if (tds.length < 2) return;

    // extract label from first td (prefer <b>)
    const labelEl = tds[0].querySelector('b');
    const rawLabel =
      labelEl?.textContent?.trim() ?? tds[0].textContent?.trim() ?? '';
    if (!rawLabel) return;

    // value is usually second td — preserve innerHTML for line breaks
    const valueHtml =
      (tds[1] as HTMLTableCellElement).innerHTML ?? tds[1].textContent ?? '';
    const valueText = htmlToTextPreserveBreaks(valueHtml);

    const normalized = rawLabel
      .replace(/:$/, '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
    mapping[normalized] = valueText;
  });

  const get = (label: string) =>
    mapping[label.toLowerCase().replace(/\s+/g, ' ').trim()] ?? '';

  function parseAddress(text: string) {
    const result = { street: '', city: '', state: '', zip: '' };
    if (!text) return result;

    const lines = text
      .split(/\r?\n/)
      .map((l) => l.replace(/ /g, ' ').trim())
      .filter(Boolean);

    // If last line contains state and zip like "ID - 83646" or "ID 83646" or "ID - 83646-1234"
    const parseStateZip = (line: string) => {
      const rz = { state: '', zip: '' };
      // common patterns: "ID - 83646", "ID 83646", "ID - 83646-1234"
      let m = line.match(/([A-Za-z]{2})\s*[-–]?\s*(\d{5}(?:-\d{4})?)/);
      if (m) {
        rz.state = m[1].toUpperCase();
        rz.zip = m[2];
        return rz;
      }
      // pattern: "City, ST ZIP"
      m = line.match(/,\s*([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)/);
      if (m) {
        rz.state = m[1].toUpperCase();
        rz.zip = m[2];
        return rz;
      }
      // pattern: "STATE - ZIP" where STATE might be full name
      m = line.match(/([A-Za-z\s]+)\s*[-–]\s*(\d{5}(?:-\d{4})?)/);
      if (m) {
        rz.state = m[1].trim();
        rz.zip = m[2];
        return rz;
      }
      return rz;
    };

    if (lines.length >= 3) {
      // assume: street (maybe multi-line), city, "ST - ZIP"
      result.street = lines.slice(0, lines.length - 2).join(', ');
      result.city = lines[lines.length - 2];
      const last = lines[lines.length - 1];
      const sz = parseStateZip(last);
      result.state = sz.state;
      result.zip = sz.zip;
      // if state missing but last line was just a city, try fallback
      if (!result.state && /^[A-Za-z]{2}$/.test(lines[lines.length - 2])) {
        result.state = lines[lines.length - 2];
      }
    } else if (lines.length === 2) {
      result.street = lines[0];
      // second line might be "CITY" or "CITY\nST - ZIP" but here it's single line
      const sz = parseStateZip(lines[1]);
      if (sz.state || sz.zip) {
        // second line contained state/zip
        // try to extract city if present before comma
        const parts = lines[1].split(',');
        result.city = parts[0].replace(/([A-Za-z]{2}\s*[-–]?)/, '').trim();
        result.state = sz.state;
        result.zip = sz.zip;
      } else {
        result.city = lines[1];
      }
    } else if (lines.length === 1) {
      // single-line address, try to parse "Street, City, ST ZIP" or "City, ST - ZIP"
      const single = lines[0];
      const parts = single.split(',');
      if (parts.length >= 3) {
        result.street = parts
          .slice(0, parts.length - 2)
          .join(',')
          .trim();
        result.city = parts[parts.length - 2].trim();
        const sz = parseStateZip(parts[parts.length - 1].trim());
        result.state = sz.state;
        result.zip = sz.zip;
      } else {
        // fallback naive parse for "City ST ZIP" or "Street ST ZIP"
        const m = single.match(/(.+)\s+([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)$/);
        if (m) {
          result.street = m[1].trim();
          result.state = m[2].toUpperCase();
          result.zip = m[3];
        } else {
          // give entire line to street as last resort
          result.street = single;
        }
      }
    }

    // final trim
    result.street = result.street?.trim() ?? '';
    result.city = result.city?.trim() ?? '';
    result.state = result.state?.trim() ?? '';
    result.zip = result.zip?.trim() ?? '';
    return result;
  }

  const premiseRaw = get('premise address');
  const mailingRaw = get('mailing address');

  const premise = parseAddress(premiseRaw);
  const mailing = parseAddress(mailingRaw);

  return {
    ffl_number: get('license number'),
    license_name: get('license name'),
    business_name: get('trade name'),
    premise_street: premise.street,
    premise_city: premise.city,
    premise_state: premise.state,
    premise_zip_code: premise.zip,
    mailing_street: mailing.street,
    mailing_city: mailing.city,
    mailing_state: mailing.state,
    mailing_zip_code: mailing.zip,
    voice_telephone: '',
    expiration_date: get('expiration date'),
    loa_issue_date: get('loa issue date'),
    loa_expiration_date: get('loa expiration date'),
  };
}

export function absolutifyFflEzUrls(
  html: string,
  base = 'https://fflezcheck.atf.gov',
): string {
  if (!html) return html;
  return html.replace(
    /(src|href)=["'](\/FFLEzCheck\/[^"']*)["']/gi,
    (_m, attr, url) => {
      return `${attr}="${base}${url}"`;
    },
  );
}

export function parseFflFirstThreeLastFive(ffl: string): {
  // 4-34-XXX-XX-XX-05811
  first: string; // 1 digit
  secondThird: string; // 2 digits
  lastFive: string; // last 5 digits
} | null {
  // Remove all non-alphanumeric characters
  const clean = ffl.replace(/[^a-zA-Z0-9]/g, '');

  // Validate length and digits in specific positions
  // Region(1) District(2) ... Sequence(5) at end
  const match = clean.match(
    /^(\d)(\d{2})[0-9X]{3}[0-9X]{2}[A-Za-z0-9X]{2}(\d{5})$/,
  );

  if (!match) {
    return null;
  }
  const [, first, secondThird, lastFive] = match;
  return {
    first,
    secondThird,
    lastFive,
  };
}

export function parseFfleZCheckResponse(response: string): boolean {
  if (response.indexOf('Expiration Date') !== -1) {
    return true;
  } else {
    return false;
  }
}
